// src/pages/rh/Employes.jsx
import React, { useState, useEffect, useMemo } from "react";
import { 
    Card, Table, Row, Col, Form, Button, Pagination, 
    Modal, Alert, Spinner, Badge, InputGroup 
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../../layouts/Footer";
import Header from "../../layouts/Header";
import { formatDate } from "../../utils/formatters";
import { API_BASE_URL } from "../../constants/api";

// Composant Toast
const ToastNotification = ({ show, onClose, type, title, message }) => {
    useEffect(() => {
        if (show) {
            const timer = setTimeout(() => onClose(), 5000);
            return () => clearTimeout(timer);
        }
    }, [show, onClose]);

    if (!show) return null;

    const toastConfig = {
        success: { bgColor: '#10b981', icon: 'ri-checkbox-circle-fill' },
        error: { bgColor: '#ef4444', icon: 'ri-error-warning-fill' },
        info: { bgColor: '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: '#f59e0b', icon: 'ri-alert-fill' }
    };

    const config = toastConfig[type] || toastConfig.info;

    return (
        <div className="toast-notification-fixed" style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 9999 }}>
            <div className="toast-modern" style={{ background: 'white', borderRadius: '16px', boxShadow: '0 20px 40px rgba(0,0,0,0.15)', minWidth: '320px', maxWidth: '400px', overflow: 'hidden', animation: 'slideInRight 0.3s ease-out' }}>
                <div className="toast-header-custom" style={{ background: config.bgColor, padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer' }}>
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom" style={{ padding: '16px 20px' }}>
                    <p className="mb-0" style={{ color: '#1e293b', fontSize: '0.9rem' }}>{message}</p>
                </div>
                <div className="toast-progress" style={{ height: '3px', background: config.bgColor, width: '100%', animation: 'shrink 5s linear forwards' }}></div>
            </div>
        </div>
    );
};

export default function Employes() {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [filterDepartement, setFilterDepartement] = useState('');
    const [filterStatut, setFilterStatut] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [employeToDelete, setEmployeToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });
    const [stats, setStats] = useState(null);

    useEffect(() => {
        fetchEmployes();
        fetchStats();
    }, []);

    const showToast = (type, title, message) => {
        setToast({ show: true, type, title, message });
    };

    const hideToast = () => {
        setToast({ show: false, type: '', title: '', message: '' });
    };

    const fetchEmployes = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/employes.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem("token");
                    localStorage.removeItem("user");
                    navigate("/pages/signin2");
                    throw new Error("Session expirée");
                }
                throw new Error(`Erreur HTTP: ${response.status}`);
            }
            
            const result = await response.json();
            setData(result.data || []);
        } catch (err) {
            setError(err.message);
            showToast('error', 'Erreur', err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/employes.php?action=stats`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur chargement stats", err);
        }
    };

    const handleDeleteClick = (employe) => {
        setEmployeToDelete(employe);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!employeToDelete) return;
        
        setDeleting(true);
        
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/employes.php?id=${employeToDelete.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            const result = await response.json();
            
            if (result.success) {
                setShowDeleteModal(false);
                setEmployeToDelete(null);
                fetchEmployes();
                fetchStats();
                showToast('success', 'Succès', "Employé supprimé avec succès");
            } else {
                showToast('error', 'Erreur', result.message || "Erreur lors de la suppression");
                setShowDeleteModal(false);
            }
        } catch (err) {
            showToast('error', 'Erreur', "Erreur de connexion au serveur");
            setShowDeleteModal(false);
        } finally {
            setDeleting(false);
        }
    };

    const getStatutBadge = (statut) => {
        switch (statut) {
            case 'actif': return { variant: 'success', text: 'Actif', icon: 'ri-checkbox-circle-line' };
            case 'inactif': return { variant: 'secondary', text: 'Inactif', icon: 'ri-user-settings-line' };
            case 'suspendu': return { variant: 'danger', text: 'Suspendu', icon: 'ri-pause-circle-line' };
            default: return { variant: 'secondary', text: 'Inconnu', icon: 'ri-question-line' };
        }
    };

    const departements = useMemo(() => {
        const deps = [...new Set(data.map(e => e.departement).filter(Boolean))];
        return deps;
    }, [data]);

    const filteredItems = useMemo(() => {
        let filtered = data.filter(item => {
            const textMatch = filterText === '' || 
                (item.nom || '').toLowerCase().includes(filterText.toLowerCase()) ||
                (item.prenom || '').toLowerCase().includes(filterText.toLowerCase()) ||
                (item.matricule || '').toLowerCase().includes(filterText.toLowerCase()) ||
                (item.email || '').toLowerCase().includes(filterText.toLowerCase()) ||
                (item.poste || '').toLowerCase().includes(filterText.toLowerCase());
            
            const deptMatch = filterDepartement === '' || item.departement === filterDepartement;
            const statutMatch = filterStatut === '' || item.statut === filterStatut;
            
            return textMatch && deptMatch && statutMatch;
        });
        
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        return filtered;
    }, [filterText, filterDepartement, filterStatut, data]);

    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentItems = filteredItems.slice(startIndex, startIndex + itemsPerPage);

    const resetFilters = () => {
        setFilterText('');
        setFilterDepartement('');
        setFilterStatut('');
        setCurrentPage(1);
    };

    if (loading && !data.length) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement des employés...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    
                    <ToastNotification 
                        show={toast.show}
                        onClose={hideToast}
                        type={toast.type}
                        title={toast.title}
                        message={toast.message}
                    />

                    {/* En-tête */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-group-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Gestion des employés</h4>
                                    <p className="text-muted small mb-0">Liste du personnel</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-primary" 
                                size="sm"
                                onClick={fetchEmployes}
                                disabled={loading}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                style={{ color: '#21ACCD', borderColor: '#21ACCD' }}
                            >
                                <i className={`ri-refresh-line ${loading ? 'fa-spin' : ''}`}></i>
                                Actualiser
                            </Button>
                            <Button 
                                style={{ background: '#21ACCD', border: '0', color: 'white' }}
                                as={Link} 
                                to="/rh/employesAjouter" 
                                size="sm"
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                            >
                                <i className="ri-add-line"></i>
                                Nouvel employé
                            </Button>
                        </div>
                    </div>

                    {/* KPI Cards */}
                    {stats && (
                        <Row className="g-3 mb-4">
                            <Col sm="6" xl="3">
                                <div className="kpi-card kpi-primary">
                                    <div className="kpi-icon"><i className="ri-group-line fs-24"></i></div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Total employés</span>
                                        <h3 className="kpi-value">{stats.total || 0}</h3>
                                        <span className="kpi-unit">Personnel total</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-card kpi-success">
                                    <div className="kpi-icon"><i className="ri-user-star-line fs-24"></i></div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Employés actifs</span>
                                        <h3 className="kpi-value text-success">{stats.actifs || 0}</h3>
                                        <span className="kpi-unit">En poste</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-card kpi-info">
                                    <div className="kpi-icon"><i className="ri-file-copy-line fs-24"></i></div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Masse salariale</span>
                                        <h3 className="kpi-value">{new Intl.NumberFormat('fr-FR').format(stats.masse_salariale || 0)} FCFA</h3>
                                        <span className="kpi-unit">Salaire mensuel</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="3">
                                <div className="kpi-card kpi-warning">
                                    <div className="kpi-icon"><i className="ri-briefcase-line fs-24"></i></div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Salaire moyen</span>
                                        <h3 className="kpi-value">{new Intl.NumberFormat('fr-FR').format(Math.round(stats.salaire_moyen || 0))} FCFA</h3>
                                        <span className="kpi-unit">Moyenne</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Filtres */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md="4">
                                    <div className="position-relative">
                                        <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-14"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher par nom, prénom, matricule..."
                                            value={filterText}
                                            onChange={e => setFilterText(e.target.value)}
                                            className="ps-5 rounded-pill"
                                        />
                                    </div>
                                </Col>
                                <Col md="3">
                                    <Form.Select 
                                        value={filterDepartement} 
                                        onChange={(e) => setFilterDepartement(e.target.value)}
                                        className="rounded-pill"
                                    >
                                        <option value="">Tous les départements</option>
                                        {departements.map(dept => (
                                            <option key={dept} value={dept}>{dept}</option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col md="2">
                                    <Form.Select 
                                        value={filterStatut} 
                                        onChange={(e) => setFilterStatut(e.target.value)}
                                        className="rounded-pill"
                                    >
                                        <option value="">Tous les statuts</option>
                                        <option value="actif">Actif</option>
                                        <option value="inactif">Inactif</option>
                                        <option value="suspendu">Suspendu</option>
                                    </Form.Select>
                                </Col>
                                <Col md="3">
                                    <div className="d-flex gap-2">
                                        <Form.Select 
                                            value={itemsPerPage} 
                                            onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                            className="rounded-pill px-3"
                                            style={{ width: '100px' }}
                                        >
                                            <option value={5}>5</option>
                                            <option value={10}>10</option>
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                        </Form.Select>
                                        {(filterText || filterDepartement || filterStatut) && (
                                            <Button 
                                                variant="outline-secondary" 
                                                size="sm"
                                                onClick={resetFilters}
                                                className="rounded-pill px-3"
                                            >
                                                <i className="ri-close-line"></i>
                                            </Button>
                                        )}
                                    </div>
                                </Col>
                            </Row>
                            <Row className="mt-2">
                                <Col className="text-end">
                                    <span className="badge bg-light text-dark rounded-pill px-3 py-2">
                                        <i className="ri-stack-line me-1"></i>
                                        {filteredItems.length} employé(s)
                                    </span>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Tableau des employés */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="border-0 py-3" style={{ background: '#21ACCD' }}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center">
                                    <i className="ri-group-line text-white me-2 fs-5"></i>
                                    <span className="fw-semibold text-white">Liste des employés</span>
                                    <Badge bg="light" text="dark" className="ms-2 rounded-pill">
                                        {filteredItems.length} au total
                                    </Badge>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0">
                                    <thead>
                                        <tr>
                                            <th className="ps-4">Matricule</th>
                                            <th>Nom & Prénom</th>
                                            <th>Poste</th>
                                            <th>Département</th>
                                            <th>Contact</th>
                                            <th>Date d'embauche</th>
                                            <th>Statut</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? currentItems.map((employe) => {
                                            const statut = getStatutBadge(employe.statut);
                                            return (
                                                <tr key={employe.id}>
                                                    <td className="ps-4">
                                                        <span className="reference-badge">{employe.matricule}</span>
                                                    </td>
                                                    <td>
                                                        <div className="fw-semibold">{employe.prenom} {employe.nom}</div>
                                                        <small className="text-muted">{employe.email}</small>
                                                    </td>
                                                    <td>{employe.poste || '-'}</td>
                                                    <td>{employe.departement || '-'}</td>
                                                    <td>
                                                        {employe.telephone && <div><i className="ri-phone-line me-1"></i>{employe.telephone}</div>}
                                                        {employe.email && <small className="text-muted">{employe.email}</small>}
                                                    </td>
                                                    <td>{formatDate(employe.date_embauche)}</td>
                                                    <td>
                                                        <Badge bg={statut.variant} className="rounded-pill">
                                                            <i className={`${statut.icon} me-1`}></i>
                                                            {statut.text}
                                                        </Badge>
                                                        {employe.nb_conges_approuves > 0 && (
                                                            <Badge bg="info" className="ms-1 rounded-pill">
                                                                {employe.nb_conges_approuves} congé(s)
                                                            </Badge>
                                                        )}
                                                    </td>
                                                    <td className="text-center pe-4">
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <button 
                                                                className="action-icon action-icon-primary" 
                                                                onClick={() => navigate(`/rh/employes/${employe.id}`)} 
                                                                title="Voir détails"
                                                            >
                                                                <i className="ri-eye-line"></i>
                                                            </button>
                                                            <button 
                                                                className="action-icon action-icon-danger" 
                                                                onClick={() => handleDeleteClick(employe)} 
                                                                title="Supprimer"
                                                            >
                                                                <i className="ri-delete-bin-line"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        }) : (
                                            <tr>
                                                <td colSpan="8" className="text-center py-5">
                                                    <i className="ri-inbox-line fs-48 text-muted mb-3 d-block"></i>
                                                    <h6 className="mb-1">Aucun employé trouvé</h6>
                                                    <p className="text-muted small mb-0">Cliquez sur "Nouvel employé" pour en ajouter</p>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* Pagination */}
                    {totalPages > 1 && !loading && (
                        <div className="d-flex justify-content-between align-items-center mt-4 px-3">
                            <div className="text-muted small">
                                Affichage de {startIndex + 1} à {Math.min(startIndex + itemsPerPage, filteredItems.length)} sur {filteredItems.length} employés
                            </div>
                            <Pagination className="mb-0">
                                <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p-1))} disabled={currentPage === 1} />
                                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                    let pageNum;
                                    if (totalPages <= 5) pageNum = i + 1;
                                    else if (currentPage <= 3) pageNum = i + 1;
                                    else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                                    else pageNum = currentPage - 2 + i;
                                    return (
                                        <Pagination.Item key={i} active={pageNum === currentPage} onClick={() => setCurrentPage(pageNum)}>
                                            {pageNum}
                                        </Pagination.Item>
                                    );
                                })}
                                <Pagination.Next onClick={() => setCurrentPage(p => Math.min(totalPages, p+1))} disabled={currentPage === totalPages} />
                            </Pagination>
                        </div>
                    )}
                </div>
                <Footer />
            </div>

            {/* Modal de suppression */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="modern-modal">
                <Modal.Header closeButton className="border-0 pb-0" style={{ background: '#dc2626' }}>
                    <Modal.Title className="w-100">
                        <div className="text-center">
                            <div className="confirm-icon mb-3">
                                <i className="ri-delete-bin-line text-white" style={{ fontSize: '4rem' }}></i>
                            </div>
                            <h3 className="fw-bold text-white">Confirmer la suppression</h3>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-4 text-center">
                    <p className="text-muted mb-3">Êtes-vous sûr de vouloir supprimer cet employé ?</p>
                    <div className="bg-light p-3 rounded-3 mb-3">
                        <strong className="text-primary d-block">{employeToDelete?.prenom} {employeToDelete?.nom}</strong>
                        <small className="text-muted">Matricule: {employeToDelete?.matricule}</small>
                        <div className="mt-1">{employeToDelete?.poste} - {employeToDelete?.departement}</div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0 pb-4 justify-content-center">
                    <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)} className="rounded-pill px-4">
                        Annuler
                    </Button>
                    <Button variant="danger" onClick={handleConfirmDelete} disabled={deleting} className="rounded-pill px-5">
                        {deleting ? <><Spinner size="sm" className="me-2" />Suppression...</> : 'Confirmer'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <style>{`
                .kpi-card { background: white; border-radius: 0px; padding: 1rem; display: flex; align-items: center; gap: 1rem; box-shadow: 0 1px 3px rgba(0,0,0,0.05); transition: all 0.2s ease; height: 100%; border-left: 2px solid; }
                .kpi-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(0,0,0,0.08); }
                .kpi-primary { border-left-color: #21ACCD; } .kpi-success { border-left-color: #10b981; } .kpi-danger { border-left-color: #ef4444; } .kpi-warning { border-left-color: #f59e0b; } .kpi-info { border-left-color: #0dcaf0; }
                .kpi-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; }
                .kpi-primary .kpi-icon { background: #e2f3f5; color: #21ACCD; }
                .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
                .kpi-danger .kpi-icon { background: #fef2f2; color: #ef4444; }
                .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
                .kpi-info .kpi-icon { background: #e2f3f5; color: #0dcaf0; }
                .kpi-content { flex: 1; }
                .kpi-label { font-size: 0.7rem; text-transform: uppercase; color: #6c757d; font-weight: 600; letter-spacing: 0.5px; }
                .kpi-value { font-size: 1.3rem; font-weight: 700; margin: 0.25rem 0; color: #1a3c5e; }
                .kpi-unit { font-size: 0.7rem; color: #6c757d; }
                .action-icon { width: 32px; height: 32px; border-radius: 8px; display: inline-flex; align-items: center; justify-content: center; background: #f8f9fa; border: 1px solid #e9ecef; color: #6c757d; transition: all 0.2s; cursor: pointer; }
                .action-icon:hover { background: #21ACCD; border-color: #21ACCD; color: white; }
                .action-icon-primary:hover { background: #21ACCD; border-color: #21ACCD; color: white; }
                .action-icon-danger:hover { background: #dc3545; border-color: #dc3545; color: white; }
                .reference-badge { background: #f8f9fa; padding: 0.2rem 0.6rem; border-radius: 6px; font-size: 0.7rem; font-family: monospace; }
                .rounded-3 { border-radius: 12px !important; }
                .rounded-pill { border-radius: 50px !important; }
                .fs-20 { font-size: 1.25rem; }
                .fs-24 { font-size: 1.5rem; }
                .fs-48 { font-size: 3rem; }
                .modern-modal .modal-content { border-radius: 20px; border: none; box-shadow: 0 20px 40px rgba(0,0,0,0.2); overflow: hidden; }
                .confirm-icon { animation: scaleIn 0.5s ease-out; }
                @keyframes scaleIn { 0% { transform: scale(0); opacity: 0; } 50% { transform: scale(1.2); } 100% { transform: scale(1); opacity: 1; } }
                @keyframes slideInRight { from { opacity: 0; transform: translateX(100%); } to { opacity: 1; transform: translateX(0); } }
                @keyframes shrink { from { width: 100%; } to { width: 0%; } }
                .toast-notification-fixed { animation: slideInRight 0.3s ease-out; }
            `}</style>
        </>
    );
}